import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Chip,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Select,
  useTheme,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useUsersAnalytics } from "../../features/users/useUsers";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";
import { Avatar, Stack } from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import BadgeIcon from "@mui/icons-material/Badge";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import PublicIcon from "@mui/icons-material/Public";
import AuthenticatorIcon from "@mui/icons-material/LockOpen";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import UserAnalyticsDetails from "../../components/UserAnalyticsDetails";

const StatCard = ({ title, subtitle, icon, children }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        boxShadow: "none",
        border: `1px solid ${theme.palette.divider}`,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minHeight: 100,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2} mb={1}>
        <Box
          variant="rounded"
          sx={{
            width: 36,
            height: 36,
            bgcolor: "transparent",
            border: `1px solid ${theme.palette.divider}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="subtitle2" color="textSecondary">
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>

      <Box mt={1}>{children}</Box>
    </Box>
  );
};
export default function UsersAnalytics() {
  const theme = useTheme();

  // State for selected month and year
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(null); // Default to "All Years"
  const [selectedMonth, setSelectedMonth] = useState(null); // Default to "All Months"

  const { data: analytics, isLoading } = useUsersAnalytics(
    selectedMonth,
    selectedYear === "all" ? null : selectedYear
  );

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  
  const yearsAvailable = Array.from(
    new Set((analytics?.series?.createdYearly || []).map((d) => d.year))
  );

  // Update selected year when years become available (only if a specific year was selected)
  useEffect(() => {
    if (
      selectedYear !== null &&
      selectedYear !== "all" &&
      yearsAvailable.length > 0 &&
      !yearsAvailable.includes(selectedYear)
    ) {
      const newYear = yearsAvailable.includes(currentYear)
        ? currentYear
        : yearsAvailable[yearsAvailable.length - 1];
      setSelectedYear(newYear);
    }
  }, [yearsAvailable, selectedYear, currentYear]);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsFilters, setDetailsFilters] = useState({});

  const handleChartClick = (data, type) => {
    if (!data) return;
    
    let value = "";
    // Handle different data structures from Recharts
    if (data.name) {
        value = data.name;
    } else if (data.payload && data.payload.name) {
        value = data.payload.name;
    } else if (data.activePayload && data.activePayload.length) {
        value = data.activePayload[0].payload.name;
    }

    // Special handling for mixed chart
    if (type === "mixed") {
        if (value.includes("Phone")) type = "hasPhone";
        else if (value.includes("Referral")) type = "hasReferral";
        else if (value.includes("IP")) type = "hasRegisterIp";
    }

    if (type === "day") {
        const day = String(data.day).padStart(2, "0");
        const month = String(selectedMonth + 1).padStart(2, "0");
        value = `${selectedYear}-${month}-${day}`;
        type = "created";
    }

    setDetailsFilters({
      type,
      value,
      year: selectedYear === null ? "all" : selectedYear,
      month: selectedMonth,
    });
    setDetailsOpen(true);
  };

  // Prepare chart data
  const chartData = (() => {
    if (selectedMonth !== null) {
      // Daily view for selected month
      return analytics?.series?.usersByDay || [];
    } else if (selectedYear !== null) {
      // Monthly view for selected year
      return months.map((m, idx) => {
        const created =
          analytics?.series?.createdMonthly?.find(
            (d) => d.year === selectedYear && d.month === idx + 1
          )?.count || 0;
        const updated =
          analytics?.series?.updatedMonthly?.find(
            (d) => d.year === selectedYear && d.month === idx + 1
          )?.count || 0;
        return { name: m.slice(0, 3), created, updated };
      });
    } else {
      // Aggregated Monthly view for All Years
      return months.map((m, idx) => {
        const created =
          analytics?.series?.createdMonthly
            ?.filter((d) => d.month === idx + 1)
            .reduce((sum, item) => sum + item.count, 0) || 0;

        const updated =
          analytics?.series?.updatedMonthly
            ?.filter((d) => d.month === idx + 1)
            .reduce((sum, item) => sum + item.count, 0) || 0;
        return { name: m.slice(0, 3), created, updated };
      });
    }
  })();

  const chartTitle =
    selectedMonth !== null
      ? `Daily Users - ${months[selectedMonth]} ${selectedYear || ""}`
      : selectedYear !== null
      ? `Monthly Users - ${selectedYear}`
      : `Monthly Users - All Years`;

  const semanticColors = {
    verified: "#2e7d32", // Green for verified
    unverified: "#d32f2f", // Red for unverified
    male: "#1976d2", // Blue for male
    female: "#c2185b", // Pink for female
    other: "#7b1fa2", // Purple for other
    google: "#4285f4", // Google blue
    facebook: "#1877f2", // Facebook blue
    auth: "#6d28d9", // Purple for auth
    phoneWith: "#2e7d32", // Green for with phone
    phoneWithout: "#d32f2f", // Red for without phone
    referralWith: "#ed6c02", // Orange for with referral
    referralWithout: "#9e9e9e", // Gray for without referral
    ipWith: "#0288d1", // Light blue for with IP
    ipWithout: "#9e9e9e", // Gray for without IP
    created: "#1976d2", // Blue for created
    updated: "#ed6c02", // Orange for updated
  };
  const formatDate = (iso) =>
    iso ? new Date(iso).toISOString().split("T")[0] : "-";

  const LoadingSkeleton = () => (
    <Box sx={{ mb: 3 }}>
      {/* Loading Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 4,
        }}
      >
        <CircularProgress
          size={24}
          sx={{ mr: 2, color: theme.palette.primary.main }}
        />
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ fontWeight: 500 }}
        >
          Loading analytics data...
        </Typography>
      </Box>

      {/* Stat Cards Skeleton */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Paper sx={{ p: 2, borderRadius: 2, opacity: 0.8 }}>
              <Skeleton
                variant="text"
                width="60%"
                height={24}
                sx={{ bgcolor: "grey.300" }}
              />
              <Skeleton
                variant="rectangular"
                height={40}
                sx={{ mt: 1, borderRadius: 1, bgcolor: "grey.300" }}
              />
              <Box sx={{ mt: 1 }}>
                <Skeleton
                  variant="text"
                  width="80%"
                  sx={{ bgcolor: "grey.300" }}
                />
                <Skeleton
                  variant="text"
                  width="70%"
                  sx={{ bgcolor: "grey.300" }}
                />
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Charts Skeleton */}
      <Grid container spacing={2}>
        {[1, 2, 3, 4].map((i) => (
          <Grid item xs={12} md={6} key={i}>
            <Paper sx={{ p: 2, borderRadius: 2, height: 300, opacity: 0.8 }}>
              <Skeleton
                variant="text"
                width="40%"
                height={32}
                sx={{ mb: 2, bgcolor: "grey.300" }}
              />
              <Skeleton
                variant="rectangular"
                height={200}
                sx={{ borderRadius: 1, bgcolor: "grey.300" }}
              />
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  return (
    <Box
      sx={{
        color: theme.palette.text.primary,
        p: 3,
        minHeight: "100vh",
        boxShadow: "none",
        border: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(2),
        borderRadius: 2,
      }}
    >
      <Container maxWidth="">
        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          analytics && (
            <Box sx={{ mb: 3 }}>
              {/* Header with Dropdowns */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                  ml: 1,
                  mt: 1,
                }}
              >
                <Typography variant="h4" fontWeight="bold">
                  User Analytics
                </Typography>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Year</InputLabel>
                    <Select
                      value={selectedYear === null ? "all" : selectedYear}
                      label="Year"
                      onChange={(e) => {
                        const val = e.target.value;
                        setSelectedYear(val === "all" ? null : Number(val));
                      }}
                    >
                      <MenuItem value="all">All Years</MenuItem>
                      {yearsAvailable.length > 0 ? (
                        yearsAvailable.map((y) => (
                          <MenuItem key={y} value={y}>
                            {y}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value={currentYear}>{currentYear}</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Month</InputLabel>
                    <Select
                      value={selectedMonth === null ? "all" : selectedMonth}
                      label="Month"
                      onChange={(e) => {
                        const val = e.target.value;
                        setSelectedMonth(val === "all" ? null : Number(val));
                      }}
                    >
                      <MenuItem value="all">All Months</MenuItem>
                      {months.map((m, idx) => (
                        <MenuItem key={idx} value={idx}>
                          {m}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              <Grid container spacing={2}>
                {/* Total Users */}
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    title="Total Users"
                    subtitle="All registered accounts"
                    icon={<PeopleIcon />}
                  >
                    <Typography variant="h5">{analytics.total ?? 0}</Typography>
                  </StatCard>
                </Grid>

                {/* Gender */}
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard title="Gender" icon={<BadgeIcon />}>
                    <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                      <Chip
                        label={`Male ${analytics.gender?.male ?? 0}`}
                        sx={{
                          backgroundColor: semanticColors.male,
                          color: "white",
                        }}
                      />
                      <Chip
                        label={`Female ${analytics.gender?.female ?? 0}`}
                        sx={{
                          backgroundColor: semanticColors.female,
                          color: "white",
                        }}
                      />
                      <Chip
                        label={`Other ${analytics.gender?.other ?? 0}`}
                        sx={{
                          backgroundColor: semanticColors.other,
                          color: "white",
                        }}
                      />
                    </Box>
                  </StatCard>
                </Grid>

                {/* Roles */}
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard title="Roles" icon={<PeopleIcon />}>
                    <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                      <Chip label={`Admin ${analytics.roles?.admin ?? 0}`} />
                      <Chip label={`User ${analytics.roles?.user ?? 0}`} />
                    </Box>
                  </StatCard>
                </Grid>

                {/* Phone Numbers */}
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard title="Phone Numbers" icon={<PhoneAndroidIcon />}>
                    <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                      <Chip
                        label={`With ${analytics.phoneNumber?.with ?? 0}`}
                        sx={{
                          backgroundColor: semanticColors.phoneWith,
                          color: "white",
                        }}
                      />
                      <Chip
                        label={`Without ${analytics.phoneNumber?.without ?? 0}`}
                        sx={{
                          backgroundColor: semanticColors.phoneWithout,
                          color: "white",
                        }}
                      />
                    </Box>
                  </StatCard>
                </Grid>

                {/* Verification */}
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard title="Verification" icon={<VerifiedUserIcon />}>
                    <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                      <Chip
                        label={`Verified ${analytics.verified?.verified ?? 0}`}
                        sx={{
                          backgroundColor: semanticColors.verified,
                          color: "white",
                        }}
                      />
                      <Chip
                        label={`Unverified ${
                          analytics.verified?.unverified ?? 0
                        }`}
                        sx={{
                          backgroundColor: semanticColors.unverified,
                          color: "white",
                        }}
                      />
                    </Box>
                  </StatCard>
                </Grid>

                {/* Referred By */}
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard title="Referred By" icon={<GroupAddIcon />}>
                    <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                      <Chip
                        label={`With ${analytics.referred_by?.with ?? 0}`}
                        sx={{
                          backgroundColor: semanticColors.referralWith,
                          color: "white",
                        }}
                      />
                      <Chip
                        label={`Without ${analytics.referred_by?.without ?? 0}`}
                        sx={{
                          backgroundColor: semanticColors.referralWithout,
                          color: "white",
                        }}
                      />
                    </Box>
                  </StatCard>
                </Grid>

                {/* Register IP */}
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard title="Register IP" icon={<PublicIcon />}>
                    <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                      <Chip
                        label={`With ${analytics.registerIpAddress?.with ?? 0}`}
                        sx={{
                          backgroundColor: semanticColors.ipWith,
                          color: "white",
                        }}
                      />
                      <Chip
                        label={`Without ${
                          analytics.registerIpAddress?.without ?? 0
                        }`}
                        sx={{
                          backgroundColor: semanticColors.ipWithout,
                          color: "white",
                        }}
                      />
                    </Box>
                  </StatCard>
                </Grid>

                {/* Auth Provider */}
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard title="Auth Provider" icon={<AuthenticatorIcon />}>
                    <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                      <Chip
                        label={`Google ${analytics.authProvider?.google ?? 0}`}
                        sx={{
                          backgroundColor: semanticColors.google,
                          color: "white",
                        }}
                      />
                      <Chip
                        label={`Facebook ${
                          analytics.authProvider?.facebook ?? 0
                        }`}
                        sx={{
                          backgroundColor: semanticColors.facebook,
                          color: "white",
                        }}
                      />
                      <Chip
                        label={`Auth ${analytics.authProvider?.auth ?? 0}`}
                        sx={{
                          backgroundColor: semanticColors.auth,
                          color: "white",
                        }}
                      />
                    </Box>
                  </StatCard>
                </Grid>

                {/* Created At */}
                <Grid item xs={12} sm={6} md={4}>
                  <StatCard title="Created At" icon={<CalendarTodayIcon />}>
                    <Box display="flex" gap={2} mt={1} flexWrap="wrap">
                      <Chip
                        label={`Earliest ${
                          analytics.timestamps?.createdAt?.earliest
                            ? formatDate(
                                analytics.timestamps.createdAt.earliest
                              )
                            : "-"
                        }`}
                      />
                      <Chip
                        label={`Latest ${
                          analytics.timestamps?.createdAt?.latest
                            ? formatDate(analytics.timestamps.createdAt.latest)
                            : "-"
                        }`}
                      />
                    </Box>
                  </StatCard>
                </Grid>

                {/* Updated At */}
                <Grid item xs={12} sm={6} md={4}>
                  <StatCard title="Updated At" icon={<CalendarTodayIcon />}>
                    <Box display="flex" gap={2} mt={1} flexWrap="wrap">
                      <Chip
                        label={`Earliest ${
                          analytics.timestamps?.updatedAt?.earliest
                            ? formatDate(
                                analytics.timestamps.updatedAt.earliest
                              )
                            : "-"
                        }`}
                      />
                      <Chip
                        label={`Latest ${
                          analytics.timestamps?.updatedAt?.latest
                            ? formatDate(analytics.timestamps.updatedAt.latest)
                            : "-"
                        }`}
                      />
                    </Box>
                  </StatCard>
                </Grid>

                {/* User Growth */}
                <Grid item xs={12} sm={6} md={4}>
                  <StatCard title="User Growth" icon={<GroupAddIcon />}>
                    <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                      <Chip
                        label={`This Month: ${
                          analytics?.series?.usersByMonth?.[
                            analytics.series.usersByMonth.length - 1
                          ]?.count || 0
                        }`}
                        sx={{
                          backgroundColor: semanticColors.created,
                          color: "white",
                        }}
                      />
                      <Chip
                        label={`Total: ${
                          analytics?.series?.usersByMonth?.[
                            analytics.series.usersByMonth.length - 1
                          ]?.cumulative ||
                          analytics.total ||
                          0
                        }`}
                        sx={{
                          backgroundColor: semanticColors.verified,
                          color: "white",
                        }}
                      />
                    </Box>
                  </StatCard>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        boxShadow: "none",
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography variant="subtitle2">Gender</Typography>
                      <Box sx={{ height: 240 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              onClick={(data) => handleChartClick(data, "gender")}
                              cursor="pointer"
                              dataKey="value"
                              data={[
                                {
                                  name: "Male",
                                  value: analytics.gender?.male || 0,
                                },
                                {
                                  name: "Female",
                                  value: analytics.gender?.female || 0,
                                },
                                {
                                  name: "Other",
                                  value: analytics.gender?.other || 0,
                                },
                              ]}
                              outerRadius={80}
                              label
                            >
                              {[
                                analytics.gender?.male || 0,
                                analytics.gender?.female || 0,
                                analytics.gender?.other || 0,
                              ].map((_, i) => (
                                <Cell
                                  key={i}
                                  fill={
                                    i === 0
                                      ? semanticColors.male
                                      : i === 1
                                      ? semanticColors.female
                                      : semanticColors.other
                                  }
                                />
                              ))}
                            </Pie>
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "#000",
                                border: "none",
                                borderRadius: "6px",
                              }}
                              itemStyle={{ color: "#fff" }}
                              labelStyle={{ color: "#fff" }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        boxShadow: "none",
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography variant="subtitle2">Verification</Typography>
                      <Box sx={{ height: 240 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={[
                              {
                                name: "Verified",
                                value: analytics.verified?.verified || 0,
                              },
                              {
                                name: "Unverified",
                                value: analytics.verified?.unverified || 0,
                              },
                            ]}
                          >
                            <XAxis dataKey="name" />
                            <YAxis allowDecimals={false} />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "#000",
                                border: "none",
                                borderRadius: "6px",
                              }}
                              itemStyle={{ color: "#fff" }}
                              labelStyle={{ color: "#fff" }}
                            />
                            <Legend />
                            <Bar
                              onClick={(data) => handleChartClick(data, "verified")}
                              cursor="pointer"
                              dataKey="value"
                              fill={(entry) =>
                                entry.name === "Verified"
                                  ? semanticColors.verified
                                  : semanticColors.unverified
                              }
                              name="Count"
                            >
                              {[
                                {
                                  name: "Verified",
                                  value: analytics.verified?.verified || 0,
                                },
                                {
                                  name: "Unverified",
                                  value: analytics.verified?.unverified || 0,
                                },
                              ].map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={
                                    entry.name === "Verified"
                                      ? semanticColors.verified
                                      : semanticColors.unverified
                                  }
                                />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        boxShadow: "none",
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography variant="subtitle2">Auth Provider</Typography>
                      <Box sx={{ height: 240 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={[
                              {
                                name: "Google",
                                value: analytics.authProvider?.google || 0,
                              },
                              {
                                name: "Facebook",
                                value: analytics.authProvider?.facebook || 0,
                              },
                              {
                                name: "Auth",
                                value: analytics.authProvider?.auth || 0,
                              },
                            ]}
                          >
                            <XAxis dataKey="name" />
                            <YAxis allowDecimals={false} />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "#000",
                                border: "none",
                                borderRadius: "6px",
                              }}
                              itemStyle={{ color: "#fff" }}
                              labelStyle={{ color: "#fff" }}
                            />
                            <Legend />
                            <Bar 
                                dataKey="value" 
                                name="Count"
                                onClick={(data) => handleChartClick(data, "authProvider")}
                                cursor="pointer"
                            >
                              {[
                                {
                                  name: "Google",
                                  value: analytics.authProvider?.google || 0,
                                },
                                {
                                  name: "Facebook",
                                  value: analytics.authProvider?.facebook || 0,
                                },
                                {
                                  name: "Auth",
                                  value: analytics.authProvider?.auth || 0,
                                },
                              ].map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={
                                    index === 0
                                      ? semanticColors.google
                                      : index === 1
                                      ? semanticColors.facebook
                                      : semanticColors.auth
                                  }
                                />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        boxShadow: "none",
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography variant="subtitle2">
                        Phone/Referral/IP Presence
                      </Typography>
                      <Box sx={{ height: 240 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={[
                              {
                                name: "Phone With",
                                value: analytics.phoneNumber?.with || 0,
                              },
                              {
                                name: "Phone Without",
                                value: analytics.phoneNumber?.without || 0,
                              },
                              {
                                name: "Referral With",
                                value: analytics.referred_by?.with || 0,
                              },
                              {
                                name: "Referral Without",
                                value: analytics.referred_by?.without || 0,
                              },
                              {
                                name: "IP With",
                                value: analytics.registerIpAddress?.with || 0,
                              },
                              {
                                name: "IP Without",
                                value:
                                  analytics.registerIpAddress?.without || 0,
                              },
                            ]}
                          >
                            <XAxis
                              dataKey="name"
                              interval={0}
                              angle={-20}
                              height={60}
                            />
                            <YAxis allowDecimals={false} />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "#000",
                                border: "none",
                                borderRadius: "6px",
                              }}
                              itemStyle={{ color: "#fff" }}
                              labelStyle={{ color: "#fff" }}
                            />
                            <Legend />
                            <Bar 
                                dataKey="value" 
                                name="Count"
                                onClick={(data) => handleChartClick(data, "mixed")}
                                cursor="pointer"
                            >
                              {[
                                {
                                  name: "Phone With",
                                  value: analytics.phoneNumber?.with || 0,
                                },
                                {
                                  name: "Phone Without",
                                  value: analytics.phoneNumber?.without || 0,
                                },
                                {
                                  name: "Referral With",
                                  value: analytics.referred_by?.with || 0,
                                },
                                {
                                  name: "Referral Without",
                                  value: analytics.referred_by?.without || 0,
                                },
                                {
                                  name: "IP With",
                                  value: analytics.registerIpAddress?.with || 0,
                                },
                                {
                                  name: "IP Without",
                                  value:
                                    analytics.registerIpAddress?.without || 0,
                                },
                              ].map((entry, index) => {
                                let color;
                                if (entry.name.includes("Phone")) {
                                  color = entry.name.includes("With")
                                    ? semanticColors.phoneWith
                                    : semanticColors.phoneWithout;
                                } else if (entry.name.includes("Referral")) {
                                  color = entry.name.includes("With")
                                    ? semanticColors.referralWith
                                    : semanticColors.referralWithout;
                                } else if (entry.name.includes("IP")) {
                                  color = entry.name.includes("With")
                                    ? semanticColors.ipWith
                                    : semanticColors.ipWithout;
                                }
                                return (
                                  <Cell key={`cell-${index}`} fill={color} />
                                );
                              })}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Main Trend Chart */}
                  <Grid item xs={12} md={12}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        boxShadow: "none",
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography variant="subtitle2">{chartTitle}</Typography>
                      <Box sx={{ height: 300 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={chartData}>
                            <XAxis
                              dataKey={selectedMonth !== null ? "day" : "name"}
                              tick={{ fontSize: 12 }}
                            />
                            <YAxis allowDecimals={false} />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "#000",
                                border: "none",
                                borderRadius: "6px",
                              }}
                              itemStyle={{ color: "#fff" }}
                              labelStyle={{ color: "#fff" }}
                            />
                            <Legend />
                            {selectedMonth !== null ? (
                              <Bar
                                dataKey="count"
                                fill={semanticColors.created}
                                name="Users Created"
                                onClick={(data) => handleChartClick(data, "day")}
                                cursor="pointer"
                              />
                            ) : (
                              <>
                                <Bar
                                  dataKey="created"
                                  fill={semanticColors.created}
                                  name="Created"
                                  onClick={(data) => handleChartClick(data, "created")}
                                  cursor="pointer"
                                />
                                <Bar
                                  dataKey="updated"
                                  fill={semanticColors.updated}
                                  name="Updated"
                                  onClick={(data) => handleChartClick(data, "updated")}
                                  cursor="pointer"
                                />
                              </>
                            )}
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )
        )}
      </Container>
      <UserAnalyticsDetails
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        filters={detailsFilters}
      />
    </Box>
  );
}
